import { Accessor, Layer, LayerContext, LayerDataSource, Position, UpdateParameters } from '@deck.gl/core';
import { Buffer, Device, Framebuffer, RenderPass, Texture } from '@luma.gl/core';
import { BufferTransform, Model } from '@luma.gl/engine';
import { ShaderModule } from '@luma.gl/shadertools';
import { timeSource } from '../../../../time/global-time-source';
import transformShader from './compute.glsl?raw';
import fs from './fragment.glsl?raw';
import vs from './vertex.glsl?raw';

export type AnimatedFlightLayerUniforms = {
    now: number;
    dt: number;
    unix_s: number;
    unix_ms: number;
    targetMode: number;
    maxProjectTimeSeconds: number;
    min_jump: number;
    max_jump: number;
};

export const animatedFlightLayerUniforms = {
    name: 'feAnimatedFlight',
    vs: `\
    layout(std140) uniform feAnimatedFlightUniforms {
        float now;
        float dt;
        uint unix_s;
        float unix_ms;
        uint targetMode;
        float maxProjectTimeSeconds;
        float min_jump;
        float max_jump;
    } feAnimatedFlight;
    `,
    defaultUniforms: {
        now: 0,
        dt: 0,
        targetMode: 0,
        unix_s: 0,
        unix_ms: 0,
        min_jump: 0.005,
        max_jump: 0.15,
        maxProjectTimeSeconds: 300
    },
    uniformTypes: {
        now: 'f32',
        dt: 'f32',
        unix_s: 'u32',
        unix_ms: 'f32',
        targetMode: 'u32',
        maxProjectTimeSeconds: 'f32',
        min_jump: 'f32',
        max_jump: 'f32'
    }
} as const satisfies ShaderModule<AnimatedFlightLayerUniforms>;

type SimLayerProps<T> = {
    data: LayerDataSource<T>;
    targetMode?: number;
    // For project coast mode
    getProjectFromPosition: Accessor<T, Position>;
    getProjectSpeed: Accessor<T, number>;
    getProjectHeading: Accessor<T, number>;
    getProjectLastUpdateTime: Accessor<T, number>;
    // For to target coast mode
    getToPosition: Accessor<T, Position>;
    getToTime: Accessor<T, number>;
    getFromPosition: Accessor<T, Position>;
    getFromTime: Accessor<T, number>;
    // Other info
    getId: Accessor<T, number>;
    getMode: Accessor<T, number>;
    unixOffsetSeconds: number;
    maxProjectTimeSeconds: number;
    minToTargetJump: number;
    maxToTargetJump: number;
    onPositionTextureChanged: (texture: Texture) => void;
};

export class CoastComputeLayer<TData> extends Layer<SimLayerProps<TData>> {
    declare state: Layer['state'] & {
        transform: BufferTransform;
        outputBuffer?: Buffer;
        fbo: Framebuffer;
        bufferToTextureModel: Model; // Don't name it "model" because it'll be picked up by the Layer base class
    };

    initializeState(): void {
        const manager = this.getAttributeManager()!;
        manager.add({
            mode: {
                size: 1,
                stepMode: 'vertex',
                type: 'uint8',
                accessor: 'getMode'
            },
            projectFromPosition: {
                size: 3,
                type: 'float32',
                stepMode: 'vertex',
                accessor: 'getProjectFromPosition'
            },
            projectSpeed: {
                size: 1,
                type: 'float32',
                stepMode: 'vertex',
                accessor: 'getProjectSpeed'
            },
            projectHeading: {
                size: 1,
                type: 'float32',
                stepMode: 'vertex',
                accessor: 'getProjectHeading'
            },
            projectLastUpdateTime: {
                size: 1,
                type: 'uint32',
                stepMode: 'vertex',
                accessor: 'getProjectLastUpdateTime'
            },
            fromPosition: {
                size: 3,
                type: 'float32',
                stepMode: 'vertex',
                accessor: 'getFromPosition'
            },
            fromTime: {
                size: 1,
                type: 'float32',
                stepMode: 'vertex',
                accessor: 'getFromTime'
            },
            toPosition: {
                size: 3,
                type: 'float32',
                stepMode: 'vertex',
                accessor: 'getToPosition'
            },
            toTime: {
                size: 1,
                type: 'float32',
                stepMode: 'vertex',
                accessor: 'getToTime'
            },
            instanceId: {
                size: 1,
                stepMode: 'vertex',
                type: 'uint32',
                accessor: 'getId'
            }
        });
    }

    updateState(params: UpdateParameters<Layer<SimLayerProps<TData>>>): void {
        super.updateState(params);
    }

    protected _postUpdate(params: UpdateParameters<Layer<SimLayerProps<TData>>>, forceUpdate: boolean): void {
        super._postUpdate(params, forceUpdate);

        if (params.changeFlags.dataChanged === 'init') {
            this.setState({
                bufferToTextureModel: new Model(params.context.device, {
                    vs,
                    fs,
                    isInstanced: false,
                    parameters: {
                        blend: false
                    },
                    topology: 'point-list',
                    bufferLayout: [
                        { name: 'position', format: 'float32x4', stepMode: 'vertex' },
                        { name: 'instanceId', format: 'sint32', stepMode: 'vertex' }
                    ],
                    vertexCount: 0
                }),
                fbo: createRenderTarget(params.context.device, 320, 320),
                transform: new BufferTransform(params.context.device, {
                    vs: transformShader,
                    bufferLayout: [
                        { name: 'mode', format: 'uint8', stepMode: 'vertex' },
                        { name: 'projectFromPosition', format: 'float32x3', stepMode: 'vertex' },
                        { name: 'projectSpeed', format: 'float32', stepMode: 'vertex' },
                        { name: 'projectHeading', format: 'float32', stepMode: 'vertex' },
                        { name: 'projectLastUpdateTime', format: 'uint32', stepMode: 'vertex' },
                        { name: 'fromPosition', format: 'float32x3', stepMode: 'vertex' },
                        { name: 'fromTime', format: 'float32', stepMode: 'vertex' },
                        { name: 'toPosition', format: 'float32x3', stepMode: 'vertex' },
                        { name: 'toTime', format: 'float32', stepMode: 'vertex' },
                        { name: 'instanceId', format: 'uint32', stepMode: 'vertex' }
                    ],
                    modules: [animatedFlightLayerUniforms],
                    outputs: ['translatedPosition'],
                    vertexCount: 0
                })
            });
            this.props.onPositionTextureChanged?.(this.state.fbo.colorAttachments[0].texture);
        }

        if (params.changeFlags.propsOrDataChanged) {
            // Update the transform
            const manager = this.getAttributeManager()!;
            const attributes = manager.getAttributes();
            const vertexCount = (this.props.data as any[]).length; // Find a better way of doing this
            this.state.transform.model.setAttributes({
                mode: attributes.mode.buffer,
                projectFromPosition: attributes.projectFromPosition.buffer,
                projectSpeed: attributes.projectSpeed.buffer,
                projectHeading: attributes.projectHeading.buffer,
                projectLastUpdateTime: attributes.projectLastUpdateTime.buffer,
                fromPosition: attributes.fromPosition.buffer,
                fromTime: attributes.fromTime.buffer,
                toPosition: attributes.toPosition.buffer,
                toTime: attributes.toTime.buffer,
                instanceId: attributes.instanceId.buffer
            });

            const newBufferSizeBytes = (attributes.projectFromPosition.buffer.byteLength / 3) * 4;
            if (!this.state.outputBuffer || newBufferSizeBytes > this.state.outputBuffer.byteLength) {
                this.state.outputBuffer?.destroy();
                const newOutputBuffer = this.context.device.createBuffer({ byteLength: newBufferSizeBytes });
                this.setState({ outputBuffer: newOutputBuffer });
            }

            this.state.transform.model.setVertexCount(vertexCount);
        }
    }

    draw(params: { renderPass: RenderPass; shaderModuleProps: any; uniforms: any; parameters: any; context: LayerContext }): void {
        const transform = this.state.transform;
        const currentTimeMs = timeSource.currentTimeMs;
        const tLast = transform.model.shaderInputs.moduleUniforms['feAnimatedFlight']['now'] as number;
        const timeNowSeconds = currentTimeMs / 1000 - this.props.unixOffsetSeconds;
        const dt = timeNowSeconds - tLast;
        transform.model.shaderInputs.setProps({
            feAnimatedFlight: {
                dt,
                now: timeNowSeconds,
                unix_s: Math.trunc(currentTimeMs / 1000),
                unix_ms: (currentTimeMs % 1000) / 1000,
                targetMode: this.props.targetMode ?? 0,
                maxProjectTimeSeconds: this.props.maxProjectTimeSeconds,
                min_jump: this.props.minToTargetJump,
                max_jump: this.props.maxToTargetJump
            } satisfies AnimatedFlightLayerUniforms
        });

        transform.model.setBindings({ positionTexture: this.state.fbo.colorAttachments[0].texture });
        transform.run({
            discard: true,
            outputBuffers: { translatedPosition: this.state.outputBuffer! }
        });

        const viewport = [0, 0, this.state.fbo.width, this.state.fbo.height] satisfies [number, number, number, number];
        const manager = this.getAttributeManager()!;
        const pass = this.context.device.beginRenderPass({
            discard: false,
            framebuffer: this.state.fbo,
            parameters: { viewport },
            clearColor: [0, 0, 0, 0],
            clearDepth: false
        });
        this.state.bufferToTextureModel.setAttributes({
            position: this.state.outputBuffer!,
            instanceId: manager.attributes.instanceId.buffer
        });
        this.state.bufferToTextureModel.setVertexCount(transform.model.vertexCount);
        this.state.bufferToTextureModel.draw(pass);

        pass.end();
    }

    finalizeState(context: LayerContext): void {
        this.state.transform.destroy();
        this.state.outputBuffer?.destroy();
        this.state.fbo.destroy();
        this.state.bufferToTextureModel.destroy();
    }
}

function createRenderTarget(device: Device, width: number, height: number): Framebuffer {
    return device.createFramebuffer({
        width,
        height,
        colorAttachments: [
            device.createTexture({
                width,
                height,
                format: 'rgba32float',
                sampler: {
                    minFilter: 'nearest',
                    magFilter: 'nearest'
                }
            })
        ]
    });
}
